import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { CompanySwitchStrategy } from '@core/http-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade, TellentOrganizationDataFacade } from '@core/tellent-logic';
import type { AddOrgPayload, SeatKindLegacy, SupportedLanguages } from '@core/tellent-types';
import { BRAND, SeatKind, TellentBrand } from '@core/tellent-types';
import { TellentComponentsNotificationService } from '@core/tellent-util';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  HeadingComponent,
  InputComponent,
  LabelComponent,
  ProductSelectableCardComponent,
  SelectComponent,
} from '@core/user-interface-legacy';

import type { AddCompanyForm } from './types';
import { CompanySettingsAnalyticsService } from '../services/company-settings.analytics';

type DialogData = {};
type AnalyticsContext = 'admin_dropdown' | 'current_user_profile';

@Component({
  selector: 'rtc-add-company-dialog',
  imports: [
    ProductSelectableCardComponent,
    FormFieldComponent,
    LabelComponent,
    SelectComponent,
    ReactiveFormsModule,
    InputComponent,
    CheckboxComponent,
    ButtonComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    DialogBodyComponent,
    TranslatePipe,
    ButtonLoadingDirective,
  ],
  templateUrl: './add-company-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './add-company-dialog.component.less',
})
export class AddCompanyDialogComponent implements DialogComponentI<DialogData, boolean> {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly dialog = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companySwitch = inject(CompanySwitchStrategy);
  private readonly tellentNotificationService = inject(TellentComponentsNotificationService);
  private readonly analytics = inject(CompanySettingsAnalyticsService);

  private readonly organizationData = inject(TellentOrganizationDataFacade);
  private readonly currentUserData = inject(TellentCurrentUserDataFacade);
  protected readonly TellentBrand = TellentBrand;
  protected readonly SeatKind = SeatKind;

  public brand = inject(BRAND);
  public loading = signal<boolean>(false);
  public selectedSeatKind: Record<SeatKindLegacy, boolean> = {
    [SeatKind.CoreHR]: false,
    [SeatKind.Recruitment]: false,
  };

  public form: FormGroup<AddCompanyForm> = new FormGroup(
    {
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
      language: new FormControl<SupportedLanguages>('en', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      apps: new FormControl<SeatKind[]>([], { nonNullable: true }),
      sampleData: new FormControl({ value: false, disabled: true }, { nonNullable: true }),
    },
    {
      validators: [form => (form.value.apps.length === 0 ? { noAppSelected: true } : null)],
    },
  );

  public links = {
    termsLink: this.i18n.translate('tlc.org.add_dialog.terms_link'),
    privacyLink: this.i18n.translate('tlc.org.add_dialog.privacy_link'),
  };

  public tellentAndCoreSupportedLanguagesOptions = [
    { value: 'en', label: this.i18n.translate('tlc.common.languages.english') },
    { value: 'nl', label: this.i18n.translate('tlc.common.languages.dutch') },
    { value: 'de', label: this.i18n.translate('tlc.common.languages.german') },
    { value: 'fr', label: this.i18n.translate('tlc.common.languages.french') },
  ];

  private analyticsContext: AnalyticsContext;

  public ngOnInit() {
    this.organizationData.addOrgSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(newCompany => {
        this.tellentNotificationService.success(
          this.i18n.translate('tlc.org.add_dialog.success_toast'),
        );

        const { guid, requestedApps } = newCompany.payload;
        this.currentUserData
          .waitForMembership(guid)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.companySwitch.switchCompany(guid, undefined, requestedApps);
            this.dialog.close(true);
          });
      });

    this.form.controls.apps.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (!!value.length) {
          this.form.controls.sampleData.enable();
          this.form.controls.sampleData.setValue(true);
        } else {
          this.form.controls.sampleData.disable();
        }
      });
  }

  public setDialogData(data: { analyticsContext: AnalyticsContext }) {
    this.analyticsContext = data.analyticsContext;
  }

  public onAddCompany() {
    this.organizationData.addOrg(<AddOrgPayload>this.form.value);
    this.analytics.trackCreateCompany(this.analyticsContext);
    this.loading.set(true);
  }

  public onCancel() {
    this.dialog.close();
  }

  public onCardClicked(seatKind: SeatKindLegacy) {
    this.selectedSeatKind[seatKind] = !this.selectedSeatKind[seatKind];

    const appsControl = this.form.controls.apps;
    appsControl.patchValue(this.getNormalizedFormValue());
    appsControl.markAsDirty();
    this.cdRef.detectChanges();
  }

  public get checkNameControlValidity() {
    const nameControl = this.form.controls.name;

    if (nameControl.valid || nameControl.untouched) return;
    if (nameControl.errors?.required) return this.i18n.translate('tlc.common.errors.required');
    if (nameControl.errors?.maxlength)
      return this.i18n.translate('tlc.common.errors.max_length', { limit: 255 });
    return '';
  }

  private getNormalizedFormValue() {
    return <SeatKind[]>Object.entries(this.selectedSeatKind)
      .filter(([_, value]) => value)
      .map(([seatKind]) => seatKind);
  }
}
